/**
 * Global react-query retry policy.
 *
 * 429 (rate-limited) gets a FEW retries with LONG exponential backoff:
 * ada-core's limiter uses a 15-minute sliding window, so a page loaded in the
 * minutes after a burst sees residual 429s that clear on their own. Never
 * retrying (the first version of this policy) wedged such pages — boot
 * queries failed once and stayed failed (no focus-refetch, no retry), leaving
 * sections permanently empty until a manual reload (observed 2026-07-10
 * 06:33, pet 68238). Retrying immediately fed the limiter and sustained the
 * storm. Backoff (2s → 8s → 30s) self-heals without adding meaningful load.
 *
 * Other 4xx (401/403/404) never succeed on retry — fail fast. Transient
 * server/network failures retry once.
 */
const RETRY_429_MAX = 3

function errorStatus(error: unknown): number | undefined {
  const status = (error as any)?.status ?? (error as any)?.body?.status
  return typeof status === 'number' ? status : undefined
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = errorStatus(error)
  if (status === 429) return failureCount < RETRY_429_MAX
  if (typeof status === 'number' && status >= 400 && status < 500) return false
  return failureCount < 1
}

/** Delay before retry `attempt` (0-based): 429 backs off 2s/8s/30s; others 1s. */
export function queryRetryDelay(attempt: number, error: unknown): number {
  if (errorStatus(error) === 429) return Math.min(2_000 * 4 ** attempt, 30_000)
  return 1_000
}
